/**
 * Utility functions for parsing and formatting trade status
 */

/**
 * Parse trade status string into array of position statuses
 * @param {string} status - Status string like "1P2O3C"
 * @returns {Array} Array of objects with position number and status
 * Example: "1P2O3C" => [{num: 1, status: 'P'}, {num: 2, status: 'O'}, {num: 3, status: 'C'}]
 */
export function parseTradeStatus(status) {
  if (!status || typeof status !== 'string') {
    return [];
  }

  const positions = [];
  const regex = /(\d+)([POC])/gi;
  let match;

  while ((match = regex.exec(status)) !== null) {
    positions.push({
      num: parseInt(match[1]),
      status: match[2].toUpperCase()
    });
  }

  return positions;
}

/**
 * Format trade status for display
 * @param {string} status - Status string like "1P2O3C"
 * @returns {string} Formatted status like "Pos1: Pending, Pos2: Open, Pos3: Closed"
 */
export function formatTradeStatus(status) {
  const positions = parseTradeStatus(status);

  if (positions.length === 0) {
    return '---';
  }

  const statusMap = {
    'P': 'Pending',
    'O': 'Open',
    'C': 'Closed'
  };

  return positions.map(pos =>
    `Pos${pos.num}: ${statusMap[pos.status] || pos.status}`
  ).join(', ');
}

/**
 * Format trade status for compact display (shorter version)
 * @param {string} status - Status string like "1P2O3C"
 * @returns {string} Formatted status like "P/O/C"
 */
export function formatTradeStatusCompact(status) {
  const positions = parseTradeStatus(status);

  if (positions.length === 0) {
    return '---';
  }

  return positions.map(pos => pos.status).join('/');
}

/**
 * Determine row color based on trade status
 * If all positions are closed -> grey
 * If any position is open -> green
 * Otherwise -> white
 * @param {string} status - Status string like "1P2O3C"
 * @returns {string} Color code
 */
export function getTradeRowColor(status) {
  const positions = parseTradeStatus(status);

  if (positions.length === 0) {
    return '#ffffff'; // default white
  }

  // If all positions are closed -> grey
  const allClosed = positions.every(pos => pos.status === 'C');
  if (allClosed) {
    return '#8b949e'; // grey
  }

  // If any position is open -> green
  const anyOpen = positions.some(pos => pos.status === 'O');
  if (anyOpen) {
    return '#3fb950'; // green
  }

  // Default for pending or other statuses -> white
  return '#ffffff'; // white
}
